import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { analyticsAPI } from '../../services/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';
import {
  TrendingUp,
  DollarSign,
  FileText,
  FolderKanban
} from 'lucide-react';

export default function AnalyticsPage() {
  const [analytics, setAnalytics] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, chartRes] = await Promise.all([
        analyticsAPI.overview(),
        analyticsAPI.chartData()
      ]);
      setAnalytics(analyticsRes.data);
      setChartData(chartRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const CHART_COLORS = ['#0F172A', '#10B981', '#64748B', '#F59E0B', '#EF4444'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 page-enter" data-testid="analytics-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Manrope']">Analytics</h1>
        <p className="text-slate-500 mt-1">Overview of all monitoring and evaluation metrics</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="stat-card bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-300 text-sm">Total Projects</p>
                <p className="text-3xl font-bold mt-1 font-['Manrope']">{analytics?.total_projects || 0}</p>
                <p className="text-emerald-400 text-sm mt-2">
                  {analytics?.active_projects || 0} active
                </p>
              </div>
              <FolderKanban className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-500 text-sm">Avg. Progress</p>
                <p className="text-3xl font-bold text-slate-900 mt-1 font-['Manrope']">
                  {analytics?.avg_progress || 0}%
                </p>
                <div className="w-full bg-slate-200 rounded-full h-2 mt-3">
                  <div
                    className="bg-emerald-500 h-2 rounded-full"
                    style={{ width: `${analytics?.avg_progress || 0}%` }}
                  ></div>
                </div>
              </div>
              <TrendingUp className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-500 text-sm">Budget Utilization</p>
                <p className="text-3xl font-bold text-slate-900 mt-1 font-['Manrope']">
                  {Math.round(analytics?.budget_utilization || 0)}%
                </p>
                <p className="text-slate-500 text-sm mt-2">
                  ${(analytics?.total_spent || 0).toLocaleString()} spent
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-500 text-sm">Field Reports</p>
                <p className="text-3xl font-bold text-slate-900 mt-1 font-['Manrope']">
                  {analytics?.total_reports || 0}
                </p>
                <p className="text-emerald-600 text-sm mt-2">
                  {analytics?.verified_reports || 0} verified
                </p>
              </div>
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Status Distribution */}
        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope']">Project Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData?.project_status?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData.project_status}
                    cx="50%"
                    cy="50%"
                    innerRadius={80}
                    outerRadius={120}
                    dataKey="count"
                    nameKey="status"
                    label={({ status, percent }) => `${status} ${(percent * 100).toFixed(0)}%`}
                  >
                    {chartData.project_status.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-slate-400">
                No project data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Progress Distribution */}
        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope']">Progress Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData?.progress_distribution?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData.progress_distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="range" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-slate-400">
                No progress data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Budget Chart */}
      <Card className="stat-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold font-['Manrope']">Budget vs Spent by Project</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData?.budget_data?.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={chartData.budget_data} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Bar dataKey="budget" name="Budget" fill="#0F172A" radius={[0, 4, 4, 0]} />
                <Bar dataKey="spent" name="Spent" fill="#10B981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[350px] text-slate-400">
              No budget data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Report Status Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="stat-card border-l-4 border-l-emerald-500">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-slate-500">Verified Reports</p>
            <p className="text-3xl font-bold text-emerald-600 mt-2 font-['Manrope']">
              {analytics?.verified_reports || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="stat-card border-l-4 border-l-amber-500">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-slate-500">Pending Reports</p>
            <p className="text-3xl font-bold text-amber-600 mt-2 font-['Manrope']">
              {analytics?.pending_reports || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="stat-card border-l-4 border-l-red-500">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-slate-500">Flagged Reports</p>
            <p className="text-3xl font-bold text-red-600 mt-2 font-['Manrope']">
              {analytics?.flagged_reports || 0}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
